import { useEffect, useMemo, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Plus, Trash2, Loader2, AlertTriangle, Save, Trash, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/authContext";
import { useLocation } from "@/lib/locationContext";
import { useAppContext } from "@/lib/onboardingContext";
import { getCurrencySymbol, convertFromUSD } from "@/lib/currency";
import { exportOperationsPdf } from "@/lib/operationsPdfExport";
import { toast } from "sonner";

const CATEGORIES = ["Produce", "Meat", "Fish", "Dairy", "Dry Goods", "Beverages", "Prepared Food"] as const;
type WasteCategory = typeof CATEGORIES[number];

interface WasteEntry {
  id: string;
  entry_date: string;
  category: WasteCategory;
  cost_usd: number;
  notes: string | null;
}

interface RevenueRow {
  entry_date: string;
  revenue_usd: number;
}

function todayStr(): string { return new Date().toISOString().split("T")[0]; }

function lastNDays(n: number): string[] {
  const out: string[] = [];
  const today = new Date();
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(today); d.setDate(d.getDate() - i);
    out.push(d.toISOString().split("T")[0]);
  }
  return out;
}

function getThisWeekRange(): { start: Date; end: Date } {
  const now = new Date();
  const day = now.getDay() === 0 ? 6 : now.getDay() - 1;
  const start = new Date(now); start.setDate(now.getDate() - day); start.setHours(0, 0, 0, 0);
  const end = new Date(start); end.setDate(start.getDate() + 7);
  return { start, end };
}

function getLastWeekRange(): { start: Date; end: Date } {
  const { start } = getThisWeekRange();
  const lastEnd = new Date(start);
  const lastStart = new Date(start); lastStart.setDate(lastStart.getDate() - 7);
  return { start: lastStart, end: lastEnd };
}

function inRange(dateStr: string, start: Date, end: Date): boolean {
  const d = new Date(dateStr);
  return d >= start && d < end;
}

export default function WasteLog() {
  const { user } = useAuth();
  const { activeLocation } = useLocation();
  const { onboarding } = useAppContext();
  const city = (activeLocation?.city || onboarding.city || "").toLowerCase();
  const cs = getCurrencySymbol(onboarding.country || city);
  const fmt = (usd: number) => `${cs}${convertFromUSD(usd, city).toFixed(2)}`;
  const fmtRound = (usd: number) => `${cs}${Math.round(convertFromUSD(usd, city)).toLocaleString()}`;

  const toUSD = useCallback((local: number) => {
    const rate = convertFromUSD(1, city);
    return rate > 0 ? local / rate : local;
  }, [city]);

  const [entries, setEntries] = useState<WasteEntry[]>([]);
  const [revenue, setRevenue] = useState<RevenueRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<{
    entry_date: string; category: WasteCategory; cost: string; notes: string;
  }>({
    entry_date: todayStr(),
    category: "Produce",
    cost: "",
    notes: "",
  });

  const load = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    setLoading(true);

    let wq = supabase.from("waste_log_entries").select("*").eq("user_id", user.id).order("entry_date", { ascending: false }).limit(120);
    if (activeLocation?.id) wq = wq.eq("location_id", activeLocation.id);
    else wq = wq.is("location_id", null);
    const { data: wRows } = await wq;
    setEntries((wRows ?? []) as WasteEntry[]);

    let rq = supabase.from("daily_revenue_entries").select("entry_date,revenue_usd").eq("user_id", user.id).limit(120);
    if (activeLocation?.id) rq = rq.eq("location_id", activeLocation.id);
    else rq = rq.is("location_id", null);
    const { data: rRows } = await rq;
    setRevenue((rRows ?? []) as RevenueRow[]);

    setLoading(false);
  }, [user, activeLocation?.id]);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setDraft({ entry_date: todayStr(), category: "Produce", cost: "", notes: "" });
    setOpen(true);
  };

  const handleSave = async () => {
    if (!user) return;
    const cost = Number(draft.cost) || 0;
    if (cost <= 0) { toast.error("Enter a waste cost greater than 0"); return; }
    setSaving(true);
    const { error } = await supabase.from("waste_log_entries").insert({
      user_id: user.id,
      location_id: activeLocation?.id ?? null,
      entry_date: draft.entry_date,
      category: draft.category,
      cost_usd: toUSD(cost),
      notes: draft.notes.trim() || null,
    });
    setSaving(false);
    if (error) { toast.error("Couldn't save entry"); return; }
    toast.success("Waste logged");
    setOpen(false);
    load();
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from("waste_log_entries").delete().eq("id", id);
    if (error) { toast.error("Couldn't delete entry"); return; }
    setEntries(prev => prev.filter(e => e.id !== id));
  };

  // Aggregations
  const thisWeek = useMemo(() => {
    const { start, end } = getThisWeekRange();
    const ws = entries.filter(e => inRange(e.entry_date, start, end));
    const total = ws.reduce((s, e) => s + Number(e.cost_usd), 0);
    const byCat: Record<string, number> = {};
    ws.forEach(e => { byCat[e.category] = (byCat[e.category] || 0) + Number(e.cost_usd); });
    const top = Object.entries(byCat).sort((a, b) => b[1] - a[1])[0];
    return { total, top: top ? { category: top[0], cost: top[1] } : null };
  }, [entries]);

  const lastWeek = useMemo(() => {
    const { start, end } = getLastWeekRange();
    const ws = entries.filter(e => inRange(e.entry_date, start, end));
    return ws.reduce((s, e) => s + Number(e.cost_usd), 0);
  }, [entries]);

  const weeklyRevenueUsd = useMemo(() => {
    const { start, end } = getThisWeekRange();
    return revenue.filter(r => inRange(r.entry_date, start, end)).reduce((s, r) => s + Number(r.revenue_usd), 0);
  }, [revenue]);

  const wastePct = weeklyRevenueUsd > 0 ? (thisWeek.total / weeklyRevenueUsd) * 100 : null;
  const overBenchmark = wastePct !== null && wastePct > 2;
  const weekDelta = lastWeek > 0 ? ((thisWeek.total - lastWeek) / lastWeek) * 100 : null;

  const chartData = useMemo(() => {
    const days = lastNDays(30);
    const map = new Map<string, number>();
    entries.forEach(e => map.set(e.entry_date, (map.get(e.entry_date) ?? 0) + Number(e.cost_usd)));
    return days.map(d => ({
      date: d.slice(5),
      waste: Math.round(convertFromUSD(map.get(d) ?? 0, city)),
    }));
  }, [entries, city]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-lg font-medium text-foreground">Waste Log</h2>
          <p className="text-sm text-muted-foreground">Track waste cost daily. Healthy benchmark is under 2% of revenue.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => {
              if (entries.length === 0) {
                toast.error("Log at least one waste entry before exporting");
                return;
              }
              const sorted = [...entries].sort((a, b) => a.entry_date.localeCompare(b.entry_date));
              const rows = sorted.map(e => [
                new Date(e.entry_date + "T00:00:00").toLocaleDateString(undefined, {
                  month: "short", day: "numeric",
                }),
                e.category,
                fmt(e.cost_usd),
                e.notes ?? "",
              ]);
              const summaryRows: Array<Array<string | number>> = [
                ["This week's waste", fmtRound(thisWeek.total)],
                ["Last week's waste", fmtRound(lastWeek)],
                ["Waste % of revenue (week)", wastePct === null ? "—" : `${wastePct.toFixed(2)}%`],
                ["Top category (week)", thisWeek.top ? `${thisWeek.top.category} (${fmtRound(thisWeek.top.cost)})` : "—"],
              ];
              exportOperationsPdf(
                {
                  title: "Waste Log Report",
                  locationName: activeLocation?.name,
                  subtitle: `${sorted.length} entries · benchmark under 2% of revenue`,
                },
                [
                  {
                    heading: "Weekly summary",
                    columns: ["Metric", "Value"],
                    alignments: ["left", "right"],
                    rows: summaryRows,
                  },
                  {
                    heading: "All entries",
                    columns: ["Date", "Category", "Cost", "Notes"],
                    alignments: ["left", "left", "right", "left"],
                    rows,
                  },
                ],
                `waste-log-${todayStr()}.pdf`,
              );
            }}
            variant="outline"
            size="sm"
            className="gap-1.5"
          >
            <FileDown className="w-4 h-4" /> Export PDF
          </Button>
          <Button onClick={openAdd} size="sm" className="gap-1.5">
            <Plus className="w-4 h-4" /> Log Waste
          </Button>
        </div>
      </div>

      {/* Weekly summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="text-xs text-muted-foreground mb-1">This week's waste</p>
          <p className="text-xl font-medium text-foreground">{fmtRound(thisWeek.total)}</p>
          {weekDelta !== null && (
            <p className={`text-xs mt-1 ${weekDelta > 0 ? "text-destructive" : "text-primary"}`}>
              {weekDelta > 0 ? "▲" : "▼"} {Math.abs(weekDelta).toFixed(0)}% vs last week
            </p>
          )}
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="text-xs text-muted-foreground mb-1">Waste as % of revenue</p>
          {wastePct === null ? (
            <p className="text-xl font-medium text-muted-foreground">—</p>
          ) : (
            <p className={`text-xl font-medium ${overBenchmark ? "text-destructive" : "text-primary"}`}>
              {wastePct.toFixed(2)}%
            </p>
          )}
          <p className="text-xs text-muted-foreground mt-1">Benchmark: under 2%</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="text-xs text-muted-foreground mb-1">Top waste category</p>
          {thisWeek.top ? (
            <>
              <p className="text-xl font-medium text-foreground">{thisWeek.top.category}</p>
              <p className="text-xs text-muted-foreground mt-1">{fmtRound(thisWeek.top.cost)} this week</p>
            </>
          ) : (
            <p className="text-xl font-medium text-muted-foreground">—</p>
          )}
        </div>
      </div>

      {overBenchmark && (
        <div className="flex items-start gap-2 bg-destructive/5 border border-destructive/30 rounded-lg p-3 text-sm">
          <AlertTriangle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
          <p className="text-foreground">
            Waste is running at <span className="font-medium text-destructive">{wastePct?.toFixed(2)}%</span> of revenue this week — above the 2% benchmark.
            Review prep portions and ordering in {thisWeek.top?.category ?? "your top category"}.
          </p>
        </div>
      )}

      {/* Trend chart */}
      <div className="bg-card border border-border rounded-xl p-4">
        <h3 className="font-heading text-sm font-medium text-foreground mb-3">30-day waste trend</h3>
        <div className="h-56 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
              <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} width={50} />
              <Tooltip
                contentStyle={{
                  background: "hsl(var(--card))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: 8,
                  fontSize: 12,
                }}
                formatter={(v: number) => `${cs}${Number(v).toLocaleString()}`}
              />
              <Line type="monotone" dataKey="waste" stroke="hsl(var(--destructive))" strokeWidth={2} dot={{ r: 2 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recent entries */}
      <div>
        <h3 className="font-heading text-sm font-medium text-foreground mb-3">Recent entries</h3>
        {entries.length === 0 ? (
          <div className="bg-card border border-border rounded-xl p-8 text-center">
            <Trash className="w-8 h-8 text-muted-foreground/40 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No waste logged yet.</p>
          </div>
        ) : (
          <div className="bg-card border border-border rounded-xl divide-y divide-border overflow-hidden">
            {entries.slice(0, 20).map(e => (
              <motion.div
                key={e.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex items-center justify-between px-4 py-3"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-foreground">{e.category}</p>
                    <Badge variant="secondary" className="text-[10px]">
                      {new Date(e.entry_date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                    </Badge>
                  </div>
                  {e.notes && <p className="text-xs text-muted-foreground mt-0.5 truncate">{e.notes}</p>}
                </div>
                <div className="flex items-center gap-3">
                  <p className="text-sm font-medium text-foreground">{fmt(e.cost_usd)}</p>
                  <button onClick={() => handleDelete(e.id)} className="text-muted-foreground hover:text-destructive p-1" aria-label="Delete entry">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Add dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Log waste</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="waste-date">Date</Label>
              <Input
                id="waste-date"
                type="date"
                value={draft.entry_date}
                onChange={e => setDraft(d => ({ ...d, entry_date: e.target.value }))}
              />
            </div>
            <div>
              <Label>Category</Label>
              <Select value={draft.category} onValueChange={(v) => setDraft(d => ({ ...d, category: v as WasteCategory }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="waste-cost">Estimated cost ({cs})</Label>
              <Input
                id="waste-cost"
                type="number"
                inputMode="decimal"
                placeholder="0.00"
                value={draft.cost}
                onChange={e => setDraft(d => ({ ...d, cost: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="waste-notes">Notes (optional)</Label>
              <Input
                id="waste-notes"
                placeholder="e.g. spoiled tomatoes from Tuesday delivery"
                value={draft.notes}
                onChange={e => setDraft(d => ({ ...d, notes: e.target.value }))}
                maxLength={200}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}